import { UploadOutlined } from '@ant-design/icons';
import { Button, Form, Input, InputNumber, Modal, Progress, Select, Tabs, Upload, message } from 'antd';
import { globalSettingService } from 'content/global-settings/services/globalSettingService';
import { LanguageInterface } from 'content/global-settings/types/globalSettingType';
import React, { useEffect, useState } from 'react';
import { videoService } from 'service/api/videoService';
import { UploadVideoInterface } from 'type/uploadVideoType';
import { I18nFieldData } from '../../../../shared/types/i18n/i18nTypes';
import LiveChannelPicker from './LiveChannelPicker';
import {
  CTA_TYPE_OPTIONS,
  CtaType,
  LIVE_SERVICE_OPTIONS,
  LiveService,
  ShortsSubtype,
  SUBTYPE_OPTIONS,
} from '../types/shortsMetaType';
import { manualUploadShortsService } from '../services/manualUploadShortsService';

interface Props {
  open: boolean;
  onClose: () => void;
  /** 업로드 성공 시 호출. 새 shorts content id 를 받아 부모가 navigate 등 처리. */
  onCreated?: (shortsContentId: number) => void;
}

/**
 * BEE-5023: "Generate Clips (Manual)" 모달.
 *
 * 흐름 — 운영자가 외부 툴(OpusClip/CapCut 등) 로 만든 9:16 mp4 를 이미
 * video_video 에 in-house 업로드해 둔 상태를 가정. 본 모달은:
 *  1) video_id (기존 Video 의 PK) 입력
 *  2) title / subtype / cta_* 등 ShortsMeta 입력
 *  3) POST /content/manual-upload-shorts/ 호출 (ucms-be #844)
 *  4) 응답으로 새 shorts_content_id 받아 onCreated 콜백.
 *
 * 자체 업로드 위젯은 별도 PR — 일단 video_video 에 이미 업로드된 자료를
 * 운영자가 골라 묶는 흐름이 가장 빠른 MVP.
 */
const ManualUploadShortsModal: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [subtype, setSubtype] = useState<ShortsSubtype>('vod');
  const [ctaType, setCtaType] = useState<CtaType>('internal');
  const [liveService, setLiveService] = useState<LiveService | null>(null);
  const [i18nDescription, setI18nDescription] = useState<I18nFieldData>({});
  const [writeLanguages, setWriteLanguages] = useState<LanguageInterface[]>([]);

  useEffect(() => {
    if (!open || writeLanguages.length) return;
    globalSettingService
      .fetchLanguages(
        new URLSearchParams({
          type: 'write',
          page_size: '100',
          order_by: 'order',
          order_direction: 'asc',
        }),
      )
      .then(r => setWriteLanguages(r.results))
      .catch(() => {
        // fetch 실패해도 fallback (ko/en) 으로 진행
        setWriteLanguages([
          { rfc_code: 'en' } as LanguageInterface,
          { rfc_code: 'ko' } as LanguageInterface,
        ]);
      });
  }, [open, writeLanguages.length]);

  // Inline file picker — videoService.uploadVideoFiles 를 한 파일에 대해 호출.
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedVideoId, setUploadedVideoId] = useState<number | null>(null);

  const reset = () => {
    form.resetFields();
    setSubtype('vod');
    setCtaType('internal');
    setLiveService(null);
    setI18nDescription({});
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadingFile(false);
    setUploadedVideoId(null);
  };

  /** antd Upload 의 beforeUpload 콜백 — 실제 multipart S3 흐름은 videoService 위임. */
  const handleFileSelected = async (file: File): Promise<void> => {
    setSelectedFile(file);
    setUploadProgress(0);
    setUploadedVideoId(null);
    setUploadingFile(true);
    // 기본 title 자동 입력 (운영자가 모달 안에서 덮어쓸 수 있음).
    const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
    if (!form.getFieldValue('title')) {
      form.setFieldsValue({ title: fileNameWithoutExt });
    }

    const videoFile: UploadVideoInterface = {
      uid: `manual-shorts-${Date.now()}`,
      name: file.name,
      size: file.size,
      type: file.type,
      originFileObj: file as any,
      percent: 0,
      speed: 0,
      state: 'normal',
      title: fileNameWithoutExt,
    };

    // 진행률 폴링 — uploadVideoFiles 자체는 store 갱신 기반이라 직접 콜백이 없으나,
    // store 대신 videoFile.percent 가 stream 단계마다 갱신되므로 인터벌로 읽어옴.
    const progressTimer = window.setInterval(() => {
      setUploadProgress(Math.floor(videoFile.percent || 0));
    }, 400);

    try {
      await videoService.uploadVideoFiles([videoFile], false);
      const videoId =
        videoFile.response && typeof videoFile.response === 'object'
          ? (videoFile.response as any).id
          : null;
      if (!videoId) {
        throw new Error('업로드는 끝났지만 video id 를 받지 못함');
      }
      setUploadedVideoId(videoId);
      setUploadProgress(100);
      form.setFieldsValue({ video_id: videoId });
      message.success(`Video 업로드 완료 — id ${videoId}`);
    } catch (e: any) {
      message.error(`Video 업로드 실패: ${e?.message || 'unknown'}`);
      setSelectedFile(null);
    } finally {
      window.clearInterval(progressTimer);
      setUploadingFile(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const res = await manualUploadShortsService.create({
        video_id: Number(values.video_id),
        title: String(values.title || '').trim(),
        slug: values.slug ? String(values.slug).trim() : undefined,
        title_en: values.title_en || undefined,
        season_id: values.season_id ?? null,
        subtype,
        cta_type: ctaType,
        internal_title: values.internal_title || undefined,
        shorts_description:
          i18nDescription?.ko ||
          Object.values(i18nDescription || {}).find(v => v) ||
          values.shorts_description ||
          null,
        i18n_shorts_description:
          i18nDescription && Object.keys(i18nDescription).length
            ? i18nDescription
            : null,
        start_timestamp:
          subtype === 'vod' && ctaType === 'internal'
            ? (values.start_timestamp ?? null)
            : null,
        cta_url: ctaType === 'external' ? values.cta_url || null : null,
        impression_tracking_url: values.impression_tracking_url || null,
        cta_channel_id:
          subtype === 'live' && ctaType === 'internal'
            ? (values.cta_channel_id ?? null)
            : null,
        live_service:
          subtype === 'live' && ctaType === 'internal' ? liveService : null,
        original_content_id:
          subtype !== 'live' ? (values.original_content_id ?? null) : null,
      });
      message.success(
        `Shorts 생성됨 (id: ${res.shorts_content_id}, transcode jobs: ${res.transcode_submitted})`,
      );
      onCreated?.(res.shorts_content_id);
      reset();
      onClose();
    } catch (e: any) {
      if (e?.errorFields) return; // antd validation
      const detail =
        e?.response?.data?.detail ||
        JSON.stringify(e?.response?.data) ||
        e?.message ||
        'unknown';
      message.error(`업로드 실패: ${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Generate Clips (Manual)"
      open={open}
      onCancel={() => {
        reset();
        onClose();
      }}
      onOk={handleSubmit}
      okText="생성"
      cancelText="취소"
      confirmLoading={submitting}
      width={640}
      destroyOnClose
    >
      <Form form={form} layout="vertical" initialValues={{ subtype: 'vod', cta_type: 'internal' }}>
        {/* Inline file picker — 새 mp4 를 즉시 업로드 + video_id 자동 입력. */}
        <Form.Item label="Video file (신규 업로드)">
          <Upload
            accept="video/mp4,video/*"
            maxCount={1}
            showUploadList={false}
            disabled={uploadingFile}
            beforeUpload={file => {
              handleFileSelected(file as File);
              return false; // antd 자체 업로드 비활성화 — videoService 가 처리
            }}
          >
            <Button
              icon={<UploadOutlined />}
              disabled={uploadingFile}
            >
              {selectedFile ? `Replace (${selectedFile.name})` : 'mp4 파일 선택'}
            </Button>
          </Upload>
          {selectedFile ? (
            <div style={{ marginTop: 8 }}>
              <Progress
                percent={uploadProgress}
                status={
                  uploadingFile
                    ? 'active'
                    : uploadedVideoId
                      ? 'success'
                      : 'exception'
                }
              />
              <div style={{ fontSize: 12, color: '#888' }}>
                {uploadedVideoId
                  ? `Video id ${uploadedVideoId} 로 업로드 완료`
                  : uploadingFile
                    ? '업로드 중…'
                    : '실패 — 다시 선택'}
              </div>
            </div>
          ) : null}
        </Form.Item>

        <Form.Item
          label="Video ID (video_video.id)"
          name="video_id"
          rules={[{ required: true, message: '파일을 업로드하거나 기존 Video id 입력' }]}
          extra="위에서 파일을 새로 업로드하면 자동 채워짐. 기존 video_video 를 골라 묶을 때만 수동 입력."
        >
          <InputNumber style={{ width: 220 }} min={1} disabled={uploadingFile} />
        </Form.Item>

        <Form.Item
          label="Title"
          name="title"
          rules={[{ required: true, message: 'title 필수' }]}
        >
          <Input placeholder="새 shorts content 제목" />
        </Form.Item>

        <Form.Item label="Slug (선택)" name="slug">
          <Input placeholder="비우면 title 기반 auto-slugify" />
        </Form.Item>

        <Form.Item label="Subtype" required>
          <Select
            value={subtype}
            onChange={v => setSubtype(v)}
            options={SUBTYPE_OPTIONS}
            style={{ width: 280 }}
          />
        </Form.Item>

        {subtype === 'vod' ? (
          <Form.Item
            label="원본 episode content id (선택)"
            name="original_content_id"
            extra="subtype=vod 일 때만 의미. ShortsMeta.original_content 로 저장."
          >
            <InputNumber style={{ width: 220 }} min={1} />
          </Form.Item>
        ) : null}

        <Form.Item label="Internal title (admin 라벨, 선택)" name="internal_title">
          <Input />
        </Form.Item>

        {/* BEE-5028: i18n description — EpisodeEdit 의 Tabs 패턴. */}
        <Form.Item label="Description (player 노출, 선택)">
          <Tabs
            type="card"
            items={(writeLanguages.length
              ? writeLanguages
              : [
                  { rfc_code: 'en' } as LanguageInterface,
                  { rfc_code: 'ko' } as LanguageInterface,
                ]
            ).map(lang => {
              const code = lang.rfc_code.toLowerCase();
              return {
                key: code,
                label: `Description ${lang.rfc_code}`,
                children: (
                  <Input.TextArea
                    rows={3}
                    placeholder="빈 값이면 episode/season/series synopsis 로 fallback"
                    value={(i18nDescription as any)[code] || ''}
                    onChange={e =>
                      setI18nDescription({
                        ...i18nDescription,
                        [code]: e.target.value,
                      } as I18nFieldData)
                    }
                  />
                ),
              };
            })}
          />
        </Form.Item>

        <Form.Item label="CTA type" required>
          <Select
            value={ctaType}
            onChange={v => setCtaType(v)}
            options={CTA_TYPE_OPTIONS}
            style={{ width: 280 }}
          />
        </Form.Item>

        {ctaType === 'internal' && subtype === 'vod' ? (
          <Form.Item label="Start timestamp (seconds)" name="start_timestamp">
            <InputNumber min={0} style={{ width: 220 }} />
          </Form.Item>
        ) : null}

        {ctaType === 'internal' && subtype === 'live' ? (
          <>
            <Form.Item label="Service" required>
              <Select
                placeholder="서비스 선택"
                value={liveService ?? undefined}
                onChange={v => {
                  setLiveService(v ?? null);
                  form.setFieldsValue({ cta_channel_id: null });
                }}
                options={LIVE_SERVICE_OPTIONS}
                style={{ width: 240 }}
              />
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                한 mediahub shorts row 는 한 서비스의 한 채널과 1:1 매핑. 양쪽
                서비스에 보내려면 row 를 2개 만드세요.
              </div>
            </Form.Item>
            {liveService ? (
              <Form.Item
                label="Channel (live deeplink)"
                name="cta_channel_id"
                rules={[{ required: true, message: 'live + internal 일 때 channel 필수' }]}
              >
                <LiveChannelPicker
                  value={form.getFieldValue('cta_channel_id') ?? null}
                  onChange={v => form.setFieldsValue({ cta_channel_id: v })}
                  service={liveService}
                />
              </Form.Item>
            ) : null}
          </>
        ) : null}

        {ctaType === 'external' ? (
          <Form.Item
            label="CTA URL (outlink)"
            name="cta_url"
            rules={[{ required: true, message: 'external 일 때 cta_url 필수' }]}
          >
            <Input placeholder="https://..." />
          </Form.Item>
        ) : null}

        <Form.Item label="Impression tracking URL (선택)" name="impression_tracking_url">
          <Input placeholder="https://tracker.example/imp?..." />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ManualUploadShortsModal;
